import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, AlertTriangle, ShieldCheck, PlayCircle, Check, X, Plus, Trash2, RefreshCw, Zap, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";

const HEALTH = {
  HEALTHY: { c: "#34D399", l: "Bonne santé" },
  WATCH: { c: "#F59E0B", l: "À surveiller" },
  RISK: { c: "#FB923C", l: "Risque" },
  CRITICAL: { c: "#FB7185", l: "Critique" },
};
const CONF = { HIGH: "text-emerald-400", MEDIUM: "text-amber-400", LOW: "text-zinc-400" };

// Libellés « simples » (compréhensibles par tous) — les codes techniques restent côté backend.
const RULE_METRICS = [
  ["CPC", "le prix d'un clic"],
  ["CPM", "le prix pour 1000 vues"],
  ["CPA", "le prix d'un résultat"],
  ["CTR", "le taux de clics (%)"],
  ["ROAS", "le retour sur dépense"],
  ["SPEND", "l'argent dépensé (€)"],
  ["CONVERSIONS", "le nombre de résultats"],
];
const RULE_OPS = [
  ["GT", "dépasse"],
  ["GTE", "atteint ou dépasse"],
  ["LT", "descend sous"],
  ["LTE", "atteint ou descend sous"],
  ["EQ", "est égal à"],
];
const RULE_ACTIONS = [
  ["PAUSE_CAMPAIGN", "⏸ Mettre en pause"],
  ["RESUME_CAMPAIGN", "▶ Relancer la campagne"],
  ["CHANGE_BUDGET", "€ Changer le budget"],
];
const RULE_METRIC_L = Object.fromEntries(RULE_METRICS);
const RULE_OP_L = Object.fromEntries(RULE_OPS);
const RULE_ACTION_L = Object.fromEntries(RULE_ACTIONS);
const ruleActionText = (t, pct) => t === "CHANGE_BUDGET"
  ? `changer le budget de ${pct > 0 ? "+" : ""}${pct}%`
  : (RULE_ACTION_L[t] || t).replace(/^[^ ]+ /, "").toLowerCase();

export default function Autopilot() {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sim, setSim] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, r] = await Promise.all([api.get("/autopilot/status"), api.post("/autopilot/analyze"), api.get("/autopilot/reports")]);
        setStatus(s.data.data); setData(a.data.data); setReports(r.data.data);
      } catch { /* 402 handled by the global PaywallGate */ }
      setLoading(false);
    })();
  }, []);

  const simulate = async (rec) => {
    const pa = rec.proposedAction || {};
    const { data } = await api.post("/autopilot/simulate", { current_state: pa.currentState || {}, proposed_state: pa.proposedState || {} });
    setSim({ rec, ...data.data });
  };
  const approve = async () => { toast.success("Recommandation approuvée — exécution externe non activée (MVP)."); setSim(null); };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const healthMap = Object.fromEntries((data.healthScores || []).map((h) => [h.campaignId, h]));

  return (
    <div className="space-y-6" data-testid="autopilot-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-amber-500">Marketing Autopilot</p>
          <h1 className="font-display text-3xl font-extrabold mt-1">L'IA surveille ton marketing</h1>
        </div>
        <span data-testid="autopilot-mode" className="px-4 h-10 inline-flex items-center rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 font-mono text-sm">Mode : {status.mode} · {status.executionCapability}</span>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/25 rounded-2xl p-3 text-xs text-amber-200/80">{data.note}</div>

      <PersistedCampaigns />
      <IntelligencePanel />
      <ActionsPanel />

      {/* Campaigns health */}
      <div className="grid gap-4 sm:grid-cols-3" data-testid="autopilot-campaigns">
        {data.campaigns.map((c) => {
          const h = healthMap[c.externalCampaignId] || {};
          const meta = HEALTH[h.status] || HEALTH.WATCH;
          const m = c.metrics || {};
          return (
            <div key={c.externalCampaignId} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={`camp-${c.externalCampaignId}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold truncate">{c.campaignName}</span>
                <span className="font-display text-lg font-extrabold" style={{ color: meta.c }}>{h.score}</span>
              </div>
              <p className="text-[11px] mt-0.5" style={{ color: meta.c }}>{meta.l}</p>
              <div className="mt-3 grid grid-cols-2 gap-1 text-xs text-zinc-400">
                <span>Dépense {m.spend}€</span>
                <span>CTR {m.ctr}%</span>
                <span>CPA {m.cpa != null ? m.cpa + "€" : "—"}</span>
                <span>Conv. {m.conversions != null ? m.conversions : "—"}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Anomalies */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="autopilot-anomalies">
        <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-xs font-mono uppercase tracking-wider text-amber-500">Anomalies détectées</span></div>
        {data.anomalies.length === 0 ? <p className="text-sm text-zinc-500">Aucune anomalie.</p> : (
          <div className="space-y-2">
            {data.anomalies.map((a, i) => (
              <div key={i} className="text-sm text-zinc-300 flex items-start gap-2" data-testid={`anomaly-${i}`}>
                <span className={a.severity === "CRITICAL" ? "text-red-400" : "text-amber-400"}>●</span>
                <span>{a.description} {a.changePercent != null && <span className="text-zinc-500">(+{a.changePercent}%)</span>}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reports */}
      {reports && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="autopilot-reports">
          <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="daily-report">
            <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Daily Brief</span>
            <p className="text-sm text-zinc-300 mt-2">{reports.daily.summary}</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-zinc-400 mt-3">
              <span>Dépense {reports.daily.totalSpend}€</span>
              <span>Conversions {reports.daily.totalConversions}</span>
              <span>CPA moyen {reports.daily.averageCpa != null ? reports.daily.averageCpa + "€" : "—"}</span>
              <span>À risque {reports.daily.campaignsAtRisk?.length ?? 0}</span>
            </div>
            {reports.daily.nextActions?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-amber-400/90 list-disc list-inside">
                {reports.daily.nextActions.map((a, i) => <li key={i}>{a.title}</li>)}
              </ul>
            )}
          </div>
          <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="weekly-report">
            <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Weekly Report</span>
            <p className="text-xs text-zinc-500 mt-1">{reports.weekly.periodStart} → {reports.weekly.periodEnd}</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-zinc-400 mt-3">
              <span>Dépense {reports.weekly.spend}€</span>
              <span>Conversions {reports.weekly.conversions}</span>
              <span>CPA {reports.weekly.cpa != null ? reports.weekly.cpa + "€" : "—"}</span>
              <span>Confiance {reports.weekly.confidence}</span>
            </div>
            {reports.weekly.keyLearnings?.length > 0 && (
              <p className="text-xs text-zinc-300 mt-3">💡 {reports.weekly.keyLearnings[0]}</p>
            )}
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className="space-y-3" data-testid="autopilot-recos">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Recommandations prioritaires</span>
        {data.recommendations.map((r, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={`reco-${i}`}>
            <div className="flex items-center gap-2">
              <span className="font-display font-extrabold text-amber-400">{r.priority}</span>
              <span className="font-semibold text-sm">{r.title}</span>
              <span className={`ml-auto text-xs ${CONF[r.confidence]}`}>confiance {r.confidence}</span>
            </div>
            <p className="text-xs text-zinc-400 mt-1">{r.reason}</p>
            <div className="flex gap-2 mt-3">
              {r.proposedAction && r.proposedAction.actionType !== "NO_ACTION" && (
                <button onClick={() => simulate(r)} data-testid={`sim-${i}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30 text-xs"><PlayCircle className="w-4 h-4" /> Simuler</button>
              )}
              <span className="inline-flex items-center px-3 h-9 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-500">{r.decision}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Simulation result */}
      {sim && (
        <div className="bg-[#0F0F13] border border-amber-500/30 rounded-2xl p-5" data-testid="simulation-result">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-amber-400" /><span className="text-sm font-bold">Simulation — {sim.rec.title}</span></div>
          <p className="text-xs text-zinc-400">Avant : {JSON.stringify(sim.simulation?.beforeState)} → Après : {JSON.stringify(sim.simulation?.afterState)}</p>
          <p className="text-xs mt-1" style={{ color: sim.simulation.spendingGuard.allowed ? "#34D399" : "#FB7185" }}>
            SpendingGuard : {sim.simulation.spendingGuard.allowed ? "✓ autorisé" : "✕ bloqué — " + sim.simulation.spendingGuard.reason}
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">{sim.simulation?.limitations?.[0]}</p>
          <div className="flex gap-2 mt-3">
            <button onClick={approve} disabled={!sim.simulation.spendingGuard.allowed} data-testid="approve-btn" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg gold-gradient text-black font-semibold text-xs disabled:opacity-40"><Check className="w-4 h-4" /> Approuver</button>
            <button onClick={() => setSim(null)} data-testid="reject-btn" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-400"><X className="w-4 h-4" /> Fermer</button>
          </div>
        </div>
      )}
      <AutopilotManage />
    </div>
  );
}

function AutopilotManage() {
  const [tab, setTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [form, setForm] = useState({ name: "", metric: "CPA", operator: "GT", value: 6, actionType: "PAUSE_CAMPAIGN", budgetChangePercent: -20 });

  const loadAll = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/autopilot/rules"), api.get("/autopilot/proposals")]);
      setRules(r.data.data.rules); setProposals(p.data.data.proposals);
    } catch { /* gated */ }
  };
  useEffect(() => { loadAll(); }, []);

  const createRule = async (e) => { e.preventDefault(); const res = await api.post("/autopilot/rules", form); if (res.data.success) { toast.success("Règle créée"); setForm({ ...form, name: "" }); loadAll(); } else toast.error(res.data.error?.message); };
  const toggleRule = async (r) => { await api.patch(`/autopilot/rules/${r.id}`, { enabled: !r.enabled }); loadAll(); };
  const delRule = async (r) => { await api.delete(`/autopilot/rules/${r.id}`); loadAll(); };
  const evaluate = async () => { const res = await api.post("/autopilot/rules/evaluate"); toast.success(`${res.data.data.count} proposition(s) générée(s)`); setTab("proposals"); loadAll(); };
  const confirmProp = async (p) => { const res = await api.post(`/autopilot/proposals/${p.id}/approve`, { confirmation: true }); if (res.data.success) toast.success("Exécuté sur le connecteur sandbox ✓"); else toast.error(res.data.error?.message || "Bloqué"); loadAll(); };
  const rejectProp = async (p) => { await api.post(`/autopilot/proposals/${p.id}/reject`, { reason: "Refusé" }); loadAll(); };

  const TABS = [["rules", "Règles"], ["proposals", "Propositions"]];
  const pending = proposals.filter((p) => p.status === "PENDING_CONFIRMATION");
  const rulePreview = `Quand ${RULE_METRIC_L[form.metric] || form.metric} ${RULE_OP_L[form.operator] || form.operator} ${form.value || "…"}, alors ${ruleActionText(form.actionType, form.budgetChangePercent)}.`;

  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="autopilot-manage">
      <div className="flex gap-2 mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3 h-9 rounded-lg text-xs font-medium border ${tab === k ? "gold-gradient text-black border-transparent" : "border-zinc-800 text-zinc-300"}`}>
            {l}{k === "proposals" && pending.length > 0 ? ` (${pending.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "rules" && (
        <div className="space-y-3" data-testid="rules-panel">
          <form onSubmit={createRule} className="bg-[#0F0F13] border border-zinc-800 rounded-xl p-4 space-y-3">
            <div>
              <label className="text-[11px] text-zinc-400">1. Donne un nom à ta règle</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Arrêter si ça coûte trop cher" data-testid="rule-name" className="mt-1 w-full h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400">2. Quand faut-il agir ?</label>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-zinc-400">Quand</span>
                <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} data-testid="rule-metric" className="h-10 px-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm">{RULE_METRICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} data-testid="rule-operator" className="h-10 px-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm">{RULE_OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <input type="number" step="any" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) })} data-testid="rule-value" className="w-24 h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400">3. Que faut-il faire ?</label>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-zinc-400">Alors</span>
                <select value={form.actionType} onChange={(e) => setForm({ ...form, actionType: e.target.value })} data-testid="rule-action" className="h-10 px-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm">{RULE_ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                {form.actionType === "CHANGE_BUDGET" && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-zinc-400">de</span>
                    <input type="number" step="any" value={form.budgetChangePercent} onChange={(e) => setForm({ ...form, budgetChangePercent: parseFloat(e.target.value) })} data-testid="rule-budget-pct" className="w-20 h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm" />
                    <span className="text-zinc-400">%</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/25 p-2.5" data-testid="rule-preview">
              <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-100/90">{rulePreview}</p>
            </div>
            <button type="submit" data-testid="rule-create" className="w-full h-10 rounded-lg gold-gradient text-black font-semibold text-sm inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Créer ma règle</button>
          </form>
          <button onClick={evaluate} data-testid="rules-evaluate" className="w-full h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-medium inline-flex items-center justify-center gap-1"><RefreshCw className="w-4 h-4" /> Vérifier mes règles maintenant</button>
          {rules.length === 0 ? <p className="text-xs text-zinc-500">Aucune règle pour l'instant. Crée ta première règle ci-dessus.</p> : rules.map((r) => (
            <div key={r.id} className="flex items-center justify-between bg-[#0F0F13] border border-zinc-800 rounded-xl p-3" data-testid={`rule-${r.id}`}>
              <div><p className="text-sm font-medium">{r.name}</p><p className="text-[11px] text-zinc-500">Quand {RULE_METRIC_L[r.conditions[0].metric] || r.conditions[0].metric} {RULE_OP_L[r.conditions[0].operator] || r.conditions[0].operator} {r.conditions[0].value} → {RULE_ACTION_L[r.action.actionType] || r.action.actionType} · réactivable après {r.cooldownHours}h</p></div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleRule(r)} data-testid={`rule-toggle-${r.id}`} className={`px-2 h-7 rounded-lg text-[11px] ${r.enabled ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-zinc-900 border border-zinc-700 text-zinc-400"}`}>{r.enabled ? "Active" : "Inactive"}</button>
                <button onClick={() => delRule(r)} data-testid={`rule-delete-${r.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "proposals" && (
        <div className="space-y-2" data-testid="proposals-panel">
          {proposals.length === 0 ? <p className="text-xs text-zinc-500">Aucune proposition. Évalue tes règles pour en générer.</p> : proposals.map((p) => (
            <div key={p.id} className="bg-[#0F0F13] border border-zinc-800 rounded-xl p-3" data-testid={`proposal-${p.id}`}>
              <div className="flex items-center gap-2"><span className="text-sm font-medium">{p.actionType}</span><span className="text-[11px] text-zinc-500">{p.platform}</span><span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${p.status === "EXECUTED" ? "text-emerald-400 border-emerald-500/30" : p.status === "PENDING_CONFIRMATION" ? "text-amber-400 border-amber-500/30" : "text-zinc-400 border-zinc-700"}`}>{p.status}</span></div>
              <p className="text-[11px] text-zinc-400 mt-1">{p.reason}</p>
              {p.spendingGuard && <p className="text-[11px] mt-1" style={{ color: p.spendingGuard.allowed ? "#34D399" : "#FB7185" }}>SpendingGuard : {p.spendingGuard.allowed ? "✓" : "✕ " + p.spendingGuard.reason}</p>}
              {p.status === "PENDING_CONFIRMATION" && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => confirmProp(p)} data-testid={`proposal-confirm-${p.id}`} className="px-3 h-8 rounded-lg gold-gradient text-black font-semibold text-xs inline-flex items-center gap-1"><Check className="w-4 h-4" /> Confirmer & exécuter (sandbox)</button>
                  <button onClick={() => rejectProp(p)} data-testid={`proposal-reject-${p.id}`} className="px-3 h-8 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs inline-flex items-center gap-1"><X className="w-4 h-4" /> Rejeter</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersistedCampaigns() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/marketing/campaigns").then((r) => setItems(r.data.campaigns)).catch(() => setItems([]));
  }, []);
  if (items === null || items.length === 0) return null;
  const SRC = { REAL: "text-emerald-400", SANDBOX: "text-amber-400", MOCK: "text-zinc-400" };
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="marketing-persisted-campaigns">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Campagnes enregistrées (Marketing AI)</span>
        <span className="text-[11px] text-zinc-500">{items.length} · historisées</span>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {items.slice(0, 30).map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 text-sm border border-zinc-800/70 rounded-lg px-3 py-2" data-testid={`mkt-camp-${c.id}`}>
            <span className="truncate text-zinc-200">{c.name}</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-700 text-zinc-400">{c.platform}</span>
              <span className={`text-[10px] ${SRC[c.source_mode] || "text-zinc-500"}`}>{c.source_mode}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 mt-2">Source analytics : PAID_ADVERTISING · mode par défaut MANUAL</p>
    </div>
  );
}

const RISK = { LOW: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", MEDIUM: "text-amber-400 border-amber-500/30 bg-amber-500/10", HIGH: "text-red-400 border-red-500/30 bg-red-500/10" };

function ActionsPanel() {
  const [props, setProps] = useState([]);
  const [hist, setHist] = useState([]);
  const load = async () => {
    try {
      const [p, h] = await Promise.all([api.get("/marketing/action-proposals"), api.get("/marketing/action-history")]);
      setProps(p.data.proposals); setHist(h.data.history);
    } catch { /* gated */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const approve = async (p) => {
    const high = p.risk_level === "HIGH";
    if (high && !window.confirm("Action à RISQUE ÉLEVÉ. Confirmer l'exécution ?")) return;
    try {
      const { data } = await api.post(`/marketing/action-proposals/${p.id}/approve`, { confirmation: true, high_risk_confirmation: high });
      toast.success(data.already ? "Déjà exécutée" : "Action exécutée ✓"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const reject = async (p) => { try { await api.post(`/marketing/action-proposals/${p.id}/reject`, {}); toast.success("Refusée"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Échec"); } };
  const pending = props.filter((p) => ["PENDING", "BLOCKED"].includes(p.status));
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="marketing-actions">
      <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Actions IA (validation manuelle)</span>
      {pending.length === 0 ? <p className="text-xs text-zinc-500 mt-2">Aucune action en attente. Les recommandations budgétaires/pause peuvent générer une proposition d'action à approuver ici.</p> : (
        <div className="space-y-2 mt-3">
          {pending.map((p) => (
            <div key={p.id} className="border border-zinc-800 rounded-xl p-3" data-testid={`mkt-action-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{p.action_type.replaceAll("_", " ")} — {p.campaign_ref}</p>
                  <p className="text-[11px] text-zinc-500">{p.platform} · {String(p.current_value)} → <b className="text-zinc-300">{String(p.proposed_value)}</b></p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${RISK[p.risk_level] || RISK.LOW}`} data-testid={`mkt-risk-${p.id}`}>Risque {p.risk_level}</span>
                  {p.confidence && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${(MKT_CONF[p.confidence] || MKT_CONF.LOW).c}`} data-testid={`mkt-conf-${p.id}`}>{(MKT_CONF[p.confidence] || MKT_CONF.LOW).t}</span>}
                </div>
              </div>
              {p.estimated_impact?.headline && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]" data-testid={`mkt-impact-${p.id}`}>
                  <TrendingUp className={`w-3.5 h-3.5 ${p.estimated_impact.direction === "down" ? "text-red-400 rotate-180" : p.estimated_impact.direction === "up" ? "text-emerald-400" : "text-zinc-400"}`} />
                  <span className="text-zinc-200 font-medium">Impact estimé : {p.estimated_impact.headline}</span>
                  {p.estimated_impact.eur_per_month != null && <span className="text-zinc-500">≈ {p.estimated_impact.eur_per_month > 0 ? "+" : ""}{p.estimated_impact.eur_per_month} €/mois</span>}
                  {p.estimated_impact.conversions_per_day != null && <span className="text-zinc-500">· {p.estimated_impact.conversions_per_day > 0 ? "+" : ""}{p.estimated_impact.conversions_per_day} conv./j</span>}
                </div>
              )}
              {p.reason && <p className="text-[11px] text-zinc-400 mt-1.5" data-testid={`mkt-why-action-${p.id}`}><span className="text-amber-400">Pourquoi :</span> {p.reason}</p>}
              <p className="text-[11px] text-zinc-500 mt-1">SpendingGuard : {p.spending_guard_result?.decision}</p>
              {p.status === "BLOCKED" ? (
                <div className="flex items-center gap-2 mt-2"><span className="text-[11px] text-red-400">Bloquée par SpendingGuard/limites</span>
                  <button onClick={() => reject(p)} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-400">Refuser</button></div>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => approve(p)} data-testid={`mkt-approve-${p.id}`} className="text-[11px] px-2.5 h-7 rounded-lg gold-gradient text-black font-semibold">Approuver & exécuter</button>
                  <button onClick={() => reject(p)} data-testid={`mkt-reject-action-${p.id}`} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-400">Refuser</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {hist.length > 0 && (
        <div className="mt-4">
          <p className="text-xs text-zinc-400 mb-1">Historique des actions IA</p>
          <div className="space-y-1">
            {hist.slice(0, 6).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-[11px] text-zinc-500 border border-zinc-800/60 rounded-lg px-2.5 py-1.5" data-testid={`mkt-hist-${h.id}`}>
                <span>{(h.created_at || "").slice(0, 16).replace("T", " ")} · {h.action_type} · {h.campaign_id}</span>
                <span className={h.execution_status === "EXECUTED" ? "text-emerald-400" : "text-red-400"}>{h.execution_status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const MKT_CONF = { HIGH: { t: "Confiance élevée", c: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" }, MEDIUM: { t: "Confiance moyenne", c: "text-amber-400 border-amber-500/30 bg-amber-500/10" }, LOW: { t: "Confiance faible", c: "text-zinc-400 border-zinc-700 bg-zinc-800" } };

function IntelligencePanel() {
  const [ins, setIns] = useState([]);
  const [recs, setRecs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/marketing/insights-all"), api.get("/marketing/recommendations-all")]);
      setIns(a.data.insights); setRecs(b.data.recommendations);
    } catch { /* gated */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const analyze = async () => {
    setBusy(true);
    try { const { data } = await api.post("/marketing/analyze"); toast.success(`Analyse : ${data.insights} insights · ${data.recommendations} recommandations${data.ai_used ? " (IA)" : ""}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'analyse"); } finally { setBusy(false); }
  };
  const act = async (r, status) => {
    try { await api.post(`/marketing/recommendations/${r.id}/status`, { status }); toast.success("Mis à jour"); load(); }
    catch { toast.error("Échec"); }
  };
  const toTask = async (r) => {
    try { const { data } = await api.post(`/marketing/recommendations/${r.id}/convert-to-task`); toast.success(data.already ? "Mission déjà créée" : "Mission créée ✓"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="marketing-intelligence">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Intelligence marketing (IA)</span>
        <button onClick={analyze} disabled={busy} data-testid="mkt-analyze-btn" className="px-3 h-8 rounded-lg text-xs font-semibold gold-gradient text-black inline-flex items-center gap-1 disabled:opacity-60">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}Analyser mes campagnes
        </button>
      </div>

      {ins.length > 0 && (
        <div className="space-y-1.5 mb-4">
          <p className="text-xs text-zinc-400 mb-1">Insights</p>
          {ins.slice(0, 6).map((i) => (
            <div key={i.id} className="border border-zinc-800/70 rounded-lg px-3 py-2" data-testid={`mkt-insight-${i.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-zinc-200 truncate">{i.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${(MKT_CONF[i.confidence] || MKT_CONF.LOW).c}`}>{(MKT_CONF[i.confidence] || MKT_CONF.LOW).t}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{i.explanation}</p>
            </div>
          ))}
        </div>
      )}

      {recs.length === 0 ? (
        <p className="text-xs text-zinc-500">Lance une analyse pour obtenir des recommandations fondées sur tes vraies performances.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-zinc-400">Recommandations</p>
          {recs.slice(0, 8).map((r) => (
            <div key={r.id} className="border border-zinc-800 rounded-xl p-3" data-testid={`mkt-reco-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-100">{r.title}</p>
                  <p className="text-[11px] text-zinc-500">{r.campaign_ref} · {r.platform}{r.status !== "NEW" ? ` · ${r.status}` : ""}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${(MKT_CONF[r.confidence] || MKT_CONF.LOW).c}`}>{(MKT_CONF[r.confidence] || MKT_CONF.LOW).t}</span>
              </div>
              <button onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))} className="text-[11px] text-amber-400 mt-1" data-testid={`mkt-why-${r.id}`}>Pourquoi Cehnura recommande cela ?</button>
              {open[r.id] && <p className="text-xs text-zinc-400 mt-1">{r.explanation}{r.data_used ? ` · Données : ${Object.entries(r.data_used).map(([k, v]) => `${k}=${v ?? "n/d"}`).join(", ")}` : ""}</p>}
              {r.status === "CONVERTED_TO_TASK" ? (
                <span className="inline-block mt-2 text-[11px] text-emerald-400">Convertie en mission ✓</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <button onClick={() => toTask(r)} data-testid={`mkt-totask-${r.id}`} className="text-[11px] px-2.5 h-7 rounded-lg gold-gradient text-black font-semibold">Créer une mission</button>
                  <button onClick={() => act(r, "ACCEPTED")} data-testid={`mkt-accept-${r.id}`} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-300">Accepter</button>
                  <button onClick={() => act(r, "POSTPONED")} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-400">Reporter</button>
                  <button onClick={() => act(r, "REJECTED")} data-testid={`mkt-reject-${r.id}`} className="text-[11px] px-2.5 h-7 rounded-lg border border-zinc-700 text-zinc-500">Ignorer</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
